import React from "react";
import loadable from "@loadable/component";
import { Redirect, RouteComponentProps, RouteProps } from "react-router-dom";

export enum RouteLayout {
  Primary,
  Secondary
}

interface BaseRouteOptions<LayoutVariant = never> {
  path: string;
  component: RouteProps["component"];
  breadcrumb?: string;

  // If true, this means that all 'sub-paths' will also be matched
  // for example if you are on: '/sign-up/select-gp' but this route's
  // path is just `/sign-up`, then a route with this set to true
  // will match but one without won't if the route path is
  hasSubRoutes?: boolean;

  // If true, this page will be accesible without being logged in
  isPublic?: boolean;

  // This is defaulted to type never (i.e. it shouldn't be present), specify a
  // type for LayoutVariant to include it in route options
  layoutVariant?: LayoutVariant;
}

export interface PrimaryRouteOptions extends BaseRouteOptions {
  layout: RouteLayout.Primary;
}

export type SecondaryLayoutVariant = "narrow" | "default";

export interface SecondaryRouteOptions
  extends BaseRouteOptions<SecondaryLayoutVariant> {
  layout: RouteLayout.Secondary;
}

export interface CustomRouteOptions extends BaseRouteOptions {}

export type RouteOptions =
  | PrimaryRouteOptions
  | SecondaryRouteOptions
  | CustomRouteOptions;

export const routes: RouteOptions[] = [
  /**
   * Account Routes
   */
  {
    path: "/account",
    component: loadable(() => import("./pages/Account")),
    layout: RouteLayout.Primary,
    breadcrumb: "Account"
  },
  {
    path: "/account/change-password",
    component: loadable(() => import("./pages/Account/ChangePassword")),
    layout: RouteLayout.Primary
  },
  {
    path: "/account/connections",
    component: loadable(() => import("./pages/Account/Connections"))
  },
  {
    path: "/account/add-patient",
    component: loadable(() => import("./pages/AddPatient"))
  },
  {
    path: "/account/add-patient/select-gp",
    component: loadable(() => import("./pages/AddPatient/SearchPractices"))
  },
  {
    path: "/account/add-patient/patient-details",
    component: loadable(() => import("./pages/AddPatient/PatientDetails"))
  },
  {
    path: "/account/wallet",
    component: loadable(() => import("./pages/Account/PaymentMethods"))
  },
  {
    path: "/account/wallet/add",
    component: loadable(() => import("./components/pages/AddPaymentMethod"))
  },
  {
    path: "/account/address",
    component: loadable(() => import("./pages/Account/DeliveryAddresses"))
  },
  {
    path: "/account/address/add",
    component: loadable(() => import("./pages/Account/AddDeliveryAddress"))
  },
  {
    path: "/account/local-collect/add",
    component: loadable(() => import("./pages/Account/CreateLocalCollect"))
  },
  {
    path: "/account/address/update",
    component: loadable(() => import("./pages/Account/UpdateDeliveryAddress"))
  },
  {
    path: "/account/details",
    component: loadable(() => import("./pages/Account/AccountDetails"))
  },
  {
    path: "/account/details/email",
    component: loadable(() => import("./pages/Account/UpdateEmail"))
  },
  {
    path: "/account/details/phone",
    component: loadable(() => import("./pages/Account/UpdatePhone"))
  },
  {
    path: "/account/patient/:patientId",
    component: loadable(() => import("./components/pages/PatientAccount")),
    layout: RouteLayout.Primary
  },
  {
    path: "/account/patient/:patientId/pharmacy",
    component: loadable(() => import("./pages/Pharmacy/Pharmacy")),
    layout: RouteLayout.Primary
  },
  {
    path: "/account/patient/:patientId/gp-surgery",
    component: loadable(() => import("./pages/Account/UpdateGpSurgery"))
  },
  {
    path: "/account/patient/:patientId/exemption",
    component: loadable(() => import("./components/pages/Exemption"))
  },
  {
    path: "/account/patient/:patientId/exemption/add",
    component: loadable(() => import("./components/pages/AddExemption"))
  },
  {
    path: "/account/patient/:patientId/exemption/add/confirm",
    component: loadable(() => import("./components/pages/ConfirmExemption"))
  },
  {
    path: "/account/patient/:patientId/demographics",
    component: loadable(() =>
      import("./pages/PatientDemographics/PatientDemographics")
    ),
    layout: RouteLayout.Secondary
  },
  {
    path: "/account/patient/:patientId/connect-gp",
    component: loadable(() => import("./pages/Account/ConnectGP")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Connect to your GP surgery"
  },

  {
    path: "/patient/:patientId/connect-gp",
    component: loadable(() => import("./pages/Account/ConnectGP")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Connect to your GP surgery"
  },
  {
    path: "/patient/:patientId/confirm-prescription",
    component: loadable(() => import("./pages/AskPatient/SelectMedications")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Confirm prescription"
  },
  {
    path: "/patient/:patientId/confirm-prescription/review",
    component: loadable(() => import("./pages/AskPatient/Review")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Review"
  },
  {
    path: "/patient/:patientId/confirm-prescription/confirmation",
    component: loadable(() => import("./pages/AskPatient/Confirmation")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Confirmation"
  },
  {
    path: "/patient/:patientId/confirm-prescription/payment-method",
    component: loadable(() => import("./pages/AskPatient/PaymentMethods")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Payment Methods"
  },
  {
    path: "/patient/:patientId/confirm-prescription/payment-method/add",
    component: loadable(() => import("./pages/AskPatient/AddPaymentMethod")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Add"
  },
  {
    path: "/patient/:patientId/confirm-prescription/delivery",
    component: loadable(() => import("./pages/AskPatient/Delivery")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Delivery"
  },
  {
    path: "/patient/:patientId/confirm-prescription/address",
    component: loadable(() => import("./pages/AskPatient/SelectAddresses")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Address"
  },
  {
    path: "/patient/:patientId/confirm-prescription/address/add",
    component: loadable(() => import("./pages/AskPatient/CreateAddress")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Add"
  },
  {
    path: "/patient/:patientId/confirm-prescription/local-collect/add",
    component: loadable(() => import("./pages/AskPatient/CreateLocalCollect")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Find a collection point"
  },
  {
    path: "/patient/:patientId/confirm-prescription/pharmacy-branch/add",
    component: loadable(() =>
      import("./pages/AskPatient/FindLloydsPharmacyBranch")
    ),
    layout: RouteLayout.Secondary,
    breadcrumb: "Find a LLoydsPharmacy"
  },
  {
    path: "/patient/:patientId/confirm-prescription/exemption",
    component: loadable(() => import("./pages/AskPatient/SelectExemption")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Select Exemption"
  },
  {
    path: "/patient/:patientId/confirm-prescription/exemption/add",
    component: loadable(() => import("./pages/AskPatient/CreateExemption")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Add"
  },
  {
    path: "/patient/:patientId/confirm-prescription/exemption/add/confirm",
    component: loadable(() =>
      import("./pages/AskPatient/CreateExemptionConfirmation")
    ),
    layout: RouteLayout.Secondary,
    breadcrumb: "Confirm"
  },

  /**
   * Medications Routes
   */
  {
    path: "/patient/:patientId/medication",
    component: loadable(() => import("./pages/Medication/PatientMedication")),
    layout: RouteLayout.Primary,
    breadcrumb: "Medicine"
  },
  {
    path: "/patient/:patientId/medication/details",
    component: loadable(() =>
      import("./pages/Medication/PatientMedicationDetails")
    ),
    layout: RouteLayout.Primary
  },
  {
    path: "/patient/:patientId/medication/search",
    component: loadable(() => import("./pages/Medication/SearchMedication"))
  },
  {
    path: "/patient/:patientId/medication/search/add",
    component: loadable(() => import("./pages/Medication/AddPatientMedication"))
  },
  {
    path: "/patient/:patientId/medication/change-due-date",
    component: loadable(() => import("./pages/Medication/UpdateNextDueDate")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Remaining Supply"
  },
  {
    path: "/patient/:patientId/medication/schedule", // not 100% sure what to call this?
    component: loadable(() => import("./pages/Medication/RemindersSummary")),
    layout: RouteLayout.Primary
  },
  {
    path: "/patient/:patientId/requests",
    component: loadable(() => import("./pages/Medication/RequestHistory")),
    layout: RouteLayout.Primary,
    // TODO: move other /requests routes (e.g. payment review) to their own routes with their own breadcrumb
    breadcrumb: "Requests"
  },
  {
    path: "/patient/:patientId/order/:orderId",
    component: loadable(() => import("./pages/Medication/RequestDetails")),
    layout: RouteLayout.Primary
  },

  /**
   * Cancel Orders
   */
  {
    path: "/cancel-requests/:patientId",
    component: loadable(() => import("./pages/CancelOrders/SelectMedications")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Cancel Requests"
  },
  {
    path: "/cancel-requests/:patientId/review",
    component: loadable(() => import("./pages/CancelOrders/Review")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Review"
  },
  {
    path: "/cancel-requests/:patientId/confirmation",
    component: loadable(() => import("./pages/CancelOrders/Confirmation")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Confirmation"
  },

  /**
   * Set delivery preference (partials)
   */
  {
    path: "/set-delivery-preference/:patientId",
    component: loadable(() =>
      import("./pages/SetDeliveryPreference/BeforeYouStart")
    ),
    layout: RouteLayout.Secondary,
    breadcrumb: "Send available items"
  },
  {
    path: "/set-delivery-preference/:patientId/review",
    component: loadable(() => import("./pages/SetDeliveryPreference/Review")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Review"
  },
  {
    path: "/set-delivery-preference/:patientId/confirmation",
    component: loadable(() =>
      import("./pages/SetDeliveryPreference/Confirmation")
    ),
    layout: RouteLayout.Secondary,
    breadcrumb: "Confirmation"
  },

  /**
   * Batch prescriptions (RDS)
   */
  {
    path: "/whats-a-batch",
    component: loadable(() => import("./pages/RDS/BatchPrescriptionInfo")),
    layout: RouteLayout.Primary
  },
  {
    // Legacy route, duplicating to redirect to the new flow
    path: "/patient/:patientId/medication/request-next-batch",
    component: (
      props: RouteComponentProps<{
        patientId: string;
      }>
    ) => (
      <Redirect
        to={`/patient/${props.match.params.patientId}/medication/request-batch`}
      />
    )
  },
  {
    path: "/patient/:patientId/medication/request-batch",
    component: loadable(() => import("./pages/RDS/SelectMedications")),
    layout: RouteLayout.Secondary,
    layoutVariant: "narrow",
    breadcrumb: "Request next batch"
  },
  {
    path: "/patient/:patientId/medication/request-batch/health-check-in",
    component: loadable(() => import("./pages/RDS/HealthCheckIn")),
    layout: RouteLayout.Secondary,
    layoutVariant: "narrow",
    breadcrumb: "Health check-in"
  },
  {
    path: "/patient/:patientId/medication/request-batch/delivery",
    component: loadable(() => import("./pages/RDS/Delivery")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Choose delivery method"
  },
  {
    path: "/patient/:patientId/medication/request-batch/address",
    component: loadable(() => import("./pages/RDS/SelectAddresses")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Address"
  },
  {
    path: "/patient/:patientId/medication/request-batch/address/add",
    component: loadable(() => import("./pages/RDS/CreateAddress")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Add"
  },
  {
    path: "/patient/:patientId/medication/request-batch/local-collect/add",
    component: loadable(() => import("./pages/RDS/CreateLocalCollect")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Find a collection point"
  },
  {
    path: "/patient/:patientId/medication/request-batch/pharmacy-branch/add",
    component: loadable(() => import("./pages/RDS/FindLloydsPharmacyBranch")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Find a LLoydsPharmacy"
  },
  {
    path: "/patient/:patientId/medication/request-batch/review",
    component: loadable(() => import("./pages/RDS/ConfirmRequest")),
    layout: RouteLayout.Secondary,
    layoutVariant: "narrow",
    breadcrumb: "Confirm request"
  },
  {
    path: "/patient/:patientId/medication/request-batch/exemption",
    component: loadable(() => import("./pages/RDS/SelectExemption")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Exemptions"
  },
  {
    path: "/patient/:patientId/medication/request-batch/exemption/add",
    component: loadable(() => import("./pages/RDS/CreateExemption")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Add"
  },
  {
    path: "/patient/:patientId/medication/request-batch/exemption/add/confirm",
    component: loadable(() =>
      import("./pages/RDS/CreateExemptionConfirmation")
    ),
    layout: RouteLayout.Secondary,
    breadcrumb: "Confirm"
  },
  {
    path: "/patient/:patientId/medication/request-batch/confirmation",
    component: loadable(() => import("./pages/RDS/AllDone")),
    layout: RouteLayout.Secondary,
    layoutVariant: "narrow",
    breadcrumb: "Confirmation"
  },

  /**
   * Return Prescriptions
   */
  {
    path: "/return-prescriptions",
    component: loadable(() =>
      import("./pages/ReturnPrescriptions/PatientPicker")
    ),
    layout: RouteLayout.Primary,
    breadcrumb: "Return Prescriptions"
  },
  {
    path: "/return-prescriptions/:patientId",
    component: loadable(() =>
      import("./pages/ReturnPrescriptions/BeforeYouStart")
    ),
    layout: RouteLayout.Secondary,
    breadcrumb: "Before you start"
  },
  {
    path: "/return-prescriptions/:patientId/prescriptions",
    component: loadable(() =>
      import("./pages/ReturnPrescriptions/SelectPrescriptions")
    ),
    layout: RouteLayout.Secondary,
    breadcrumb: "Select Prescriptions"
  },
  {
    path: "/return-prescriptions/:patientId/review",
    component: loadable(() =>
      import("./pages/ReturnPrescriptions/ReviewReturns")
    ),
    layout: RouteLayout.Secondary,
    breadcrumb: "Review Prescriptions"
  },
  {
    path: "/return-prescriptions/:patientId/confirmation",
    component: loadable(() =>
      import("./pages/ReturnPrescriptions/ReturnComplete")
    ),
    layout: RouteLayout.Secondary,
    breadcrumb: "Return Confirmed"
  },

  /**
   * Request prescriptions
   */
  {
    path: "/patient/:patientId/medication/request-prescription",
    component: loadable(() => import("./pages/CreateOrders/SelectMedications")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Request"
  },
  {
    path: "/patient/:patientId/medication/request-prescription/review",
    component: loadable(() => import("./pages/CreateOrders/Create")),
    layout: RouteLayout.Secondary,
    layoutVariant: "narrow",
    breadcrumb: "Review"
  },
  {
    path: "/patient/:patientId/medication/request-prescription/confirmation",
    component: loadable(() => import("./pages/CreateOrders/Confirmation")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Confirmation"
  },
  {
    path: "/patient/:patientId/medication/request-prescription/delivery",
    component: loadable(() => import("./pages/CreateOrders/Delivery")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Delivery"
  },
  {
    path: "/patient/:patientId/medication/request-prescription/select-addons",
    component: loadable(() => import("./pages/CreateOrders/AddToDelivery")),
    layout: RouteLayout.Secondary,
    layoutVariant: "narrow",
    breadcrumb: "Add to your delivery"
  },
  {
    path: "/patient/:patientId/medication/request-prescription/address",
    component: loadable(() => import("./pages/CreateOrders/SelectAddress")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Delivery Address"
  },
  {
    path: "/patient/:patientId/medication/request-prescription/address/add",
    component: loadable(() => import("./pages/CreateOrders/CreateAddress")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Add"
  },
  {
    path:
      "/patient/:patientId/medication/request-prescription/local-collect/add",
    component: loadable(() =>
      import("./pages/CreateOrders/CreateLocalCollect")
    ),
    layout: RouteLayout.Secondary,
    breadcrumb: "Find a collection point"
  },
  {
    path:
      "/patient/:patientId/medication/request-prescription/pharmacy-branch/add",
    component: loadable(() =>
      import("./pages/CreateOrders/FindLloydsPharmacyBranch")
    ),
    layout: RouteLayout.Secondary,
    breadcrumb: "Find a LLoydsPharmacy"
  },
  {
    path: "/patient/:patientId/medication/request-prescription/exemption",
    component: loadable(() => import("./pages/CreateOrders/SelectExemption")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Exemptions"
  },
  {
    path: "/patient/:patientId/medication/request-prescription/exemption/add",
    component: loadable(() => import("./pages/CreateOrders/CreateExemption")),
    layout: RouteLayout.Secondary,
    breadcrumb: "Add"
  },
  {
    path:
      "/patient/:patientId/medication/request-prescription/exemption/add/confirm",
    component: loadable(() =>
      import("./pages/CreateOrders/CreateExemptionConfirmation")
    ),
    layout: RouteLayout.Secondary,
    breadcrumb: "Confirm"
  },
  {
    path: "/patient/:patientId/medication/request-prescription/payment-method",
    component: loadable(() =>
      import("./components/pages/MedicationSelectPaymentMethod")
    )
  },
  {
    path:
      "/patient/:patientId/medication/request-prescription/payment-method/add",
    component: loadable(() =>
      import("./components/pages/MedicationAddPaymentMethod")
    )
  },

  /**
   * Requests Routes
   */
  {
    path: "/patient/:patientId/requests/pay",
    component: loadable(() => import("./components/pages/PaymentReview")),
    layout: RouteLayout.Secondary,
    layoutVariant: "narrow",
    breadcrumb: "Payment"
  },
  {
    path: "/patient/:patientId/requests/pay/change-addons-selection",
    component: loadable(() => import("./pages/CreateOrders/ChangeSelection")),
    layout: RouteLayout.Secondary,
    layoutVariant: "narrow",
    breadcrumb: "Add to your delivery"
  },
  {
    path: "/patient/:patientId/requests/pay/payment-method",
    component: loadable(() => import("./components/pages/SelectPaymentMethod"))
  },
  {
    path: "/patient/:patientId/requests/pay/payment-method/add",
    component: loadable(() =>
      import("./components/pages/RequestsAddPaymentMethod")
    )
  },
  {
    path: "/patient/:patientId/requests/pay/confirmation",
    component: loadable(() => import("./components/pages/PaymentConfirmation"))
  },
  {
    path: "/patient/:patientId/requests/exempt-confirmation",
    component: loadable(() => import("./pages/Payment/ExemptConfirmation"))
  },
  {
    path: "/patient/:patientId/arrange-delivery",
    component: loadable(() =>
      import("./pages/Partners/ArrangeDelivery/SelectMedications")
    )
  },
  {
    path: "/patient/:patientId/arrange-delivery/review",
    component: loadable(() => import("./pages/Partners/ArrangeDelivery/Review"))
  },
  {
    path: "/patient/:patientId/arrange-delivery/delivery",
    component: loadable(() =>
      import("./pages/Partners/ArrangeDelivery/Delivery")
    )
  },
  {
    path: "/patient/:patientId/arrange-delivery/address",
    component: loadable(() =>
      import("./pages/Partners/ArrangeDelivery/SelectAddresses")
    )
  },
  {
    path: "/patient/:patientId/arrange-delivery/address/add",
    component: loadable(() =>
      import("./pages/Partners/ArrangeDelivery/CreateAddress")
    )
  },
  {
    path: "/patient/:patientId/arrange-delivery/local-collect/add",
    component: loadable(() =>
      import("./pages/Partners/ArrangeDelivery/CreateLocalCollect")
    )
  },
  {
    path: "/patient/:patientId/arrange-delivery/exemption",
    component: loadable(() =>
      import("./pages/Partners/ArrangeDelivery/SelectExemption")
    )
  },
  {
    path: "/patient/:patientId/arrange-delivery/exemption/add",
    component: loadable(() =>
      import("./pages/Partners/ArrangeDelivery/CreateExemption")
    )
  },
  {
    path: "/patient/:patientId/arrange-delivery/exemption/add/confirm",
    component: loadable(() =>
      import("./pages/Partners/ArrangeDelivery/CreateExemptionConfirmation")
    )
  },
  {
    path: "/patient/:patientId/arrange-delivery/confirmation",
    component: loadable(() =>
      import("./pages/Partners/ArrangeDelivery/Confirmation")
    )
  },
  {
    path: "/patient/:patientId/arrange-delivery/payment-method",
    component: loadable(() =>
      import("./pages/Partners/ArrangeDelivery/PaymentMethods")
    )
  },
  {
    path: "/patient/:patientId/arrange-delivery/payment-method/add",
    component: loadable(() =>
      import("./pages/Partners/ArrangeDelivery/AddPaymentMethod")
    )
  },

  /**
   * Help
   */
  {
    path: "/help",
    component: loadable(() => import("./pages/SelfHelp/PatientPicker")),
    layout: RouteLayout.Primary
  },
  {
    path: "/help/:patientId",
    component: loadable(() => import("./pages/SelfHelp/Node")),
    layout: RouteLayout.Primary
  },
  {
    path: "/help/:patientId/contact",
    component: loadable(() => import("./pages/SelfHelp/Contact")),
    layout: RouteLayout.Primary
  },

  /**
   * What's new
   */
  {
    path: "/whats-new",
    component: loadable(() => import("./pages/WhatsNew")),
    layout: RouteLayout.Primary
  },
  {
    path: "/whats-new/:slug",
    component: loadable(() => import("./pages/WhatsNew/post")),
    layout: RouteLayout.Primary
  },

  /** Sign up */
  {
    path: "/sign-up",
    component: loadable(() => import("./pages/SignUp")),
    isPublic: true
  },
  {
    path: "/sign-up/select-gp",
    component: loadable(() => import("./pages/SignUp/SearchPractices")),
    isPublic: true
  },
  {
    path: "/sign-up/create-account",
    component: loadable(() => import("./pages/SignUp/CreateAccount")),
    isPublic: true
  },
  {
    path: "/sign-up/create-patient",
    component: loadable(() => import("./pages/SignUp/PatientDetails"))
  },
  {
    path: "/sign-up/awaiting-verification",
    component: loadable(() => import("./pages/SignUp/AwaitingVerification"))
  },

  /** Sign up V2 */

  {
    path: "/v2/sign-up/confirm-email-redirect",
    component: loadable(() => import("./pages/SignUpV2/ConfirmEmailRedirect")),
    isPublic: true
  },
  {
    path: "/v2/sign-up",
    component: loadable(() => import("./pages/SignUpV2")),
    isPublic: true
  },
  {
    path: "/v2/sign-up/create-account",
    component: loadable(() => import("./pages/SignUpV2/CreateAccount")),
    isPublic: true
  },
  {
    path: "/v2/sign-up/confirm-email",
    component: loadable(() => import("./pages/SignUpV2/ConfirmEmail"))
  },
  {
    path: "/v2/sign-up/add-phone",
    component: loadable(() => import("./pages/SignUpV2/AddPhone"))
  },
  {
    path: "/v2/sign-up/confirm-phone",
    component: loadable(() => import("./pages/SignUpV2/ConfirmPhone"))
  },
  {
    path: "/v2/sign-up/checkpoint",
    component: loadable(() => import("./pages/SignUpV2/CheckPoint"))
  },
  {
    path: "/v2/sign-up/nhs-number",
    component: loadable(() => import("./pages/SignUpV2/NHSNumber"))
  },
  {
    path: "/v2/sign-up/find-patient",
    component: loadable(() => import("./pages/SignUpV2/FindPatient"))
  },
  {
    path: "/v2/sign-up/patient-details",
    component: loadable(() => import("./pages/SignUpV2/PatientDetails"))
  },
  {
    path: "/v2/sign-up/gp-search",
    component: loadable(() => import("./pages/SignUpV2/GPSearch"))
  },
  {
    path: "/v2/sign-up/gp-search-confirm",
    component: loadable(() => import("./pages/SignUpV2/GPSearchConfirm"))
  },
  {
    path: "/v2/sign-up/gp-no-third-party",
    component: loadable(() => import("./pages/SignUpV2/GPNoThirdParty"))
  },
  {
    path: "/v2/sign-up/gp-not-supported",
    component: loadable(() => import("./pages/SignUpV2/GPNotSupported"))
  },
  {
    path: "/v2/sign-up/gp-no-eps",
    component: loadable(() => import("./pages/SignUpV2/GPNoEPS"))
  },
  {
    path: "/v2/sign-up/confirm-gp",
    component: loadable(() => import("./pages/SignUpV2/ConfirmGP"))
  },
  {
    path: "/v2/sign-up/nomination",
    component: loadable(() => import("./pages/SignUpV2/Nomination"))
  },
  {
    path: "/v2/sign-up/contact-patient-care",
    component: loadable(() => import("./pages/SignUpV2/ContactPatientCare"))
  },

  /**
   * Referrals
   */
  {
    path: "/invite-a-friend",
    component: loadable(() => import("./pages/Referrals/Referrals")),
    layout: RouteLayout.Primary
  },
  {
    path: "/invite-a-friend/choose-reward",
    component: loadable(() => import("./pages/Referrals/ChooseReward")),
    layout: RouteLayout.Primary
  },
  {
    path: "/invite-a-friend/choose-reward/success",
    component: loadable(() => import("./pages/Referrals/ChooseRewardSuccess")),
    layout: RouteLayout.Primary
  },

  /**
   * Partners connect flow
   */
  {
    path: "/connect",
    component: () => <Redirect to="/connect/welcome" />,
    isPublic: true
  },
  {
    path: "/connect/welcome",
    component: loadable(() => import("./pages/Partners/Connect/Welcome")),
    isPublic: true
  },
  {
    path: "/connect/create-account",
    component: loadable(() =>
      import("./pages/Partners/Connect/AccountDetails")
    ),
    isPublic: true
  },
  {
    path: "/connect/address",
    component: loadable(() =>
      import("./pages/Partners/Connect/RegisteredAddress")
    ),
    isPublic: true
  },
  {
    path: "/connect/consent",
    component: loadable(() => import("./pages/Partners/Connect/ConsentCheck")),
    isPublic: true
  },
  {
    path: "/connect/awaiting-email-verification",
    component: loadable(() =>
      import("./pages/Partners/Connect/FinishConnectAccount")
    ),
    isPublic: true
  },
  {
    path: "/connect/verify",
    component: loadable(() =>
      import("./pages/Partners/Connect/VerifyConnectAccount")
    ),
    isPublic: true
  },
  {
    path: "/connect/verify-email",
    component: loadable(() => import("./pages/Partners/Connect/VerifyEmail")),
    isPublic: true
  },

  /**
   * Logged out routes
   * TODO: Some of these should probably be somwhere else and are overly 'flat'.
   */
  {
    path: "/reset-password",
    component: loadable(() => import("./components/pages/ResetPasswordPage")),
    isPublic: true
  },
  {
    path: "/forgot-password",
    component: loadable(() => import("./components/pages/ForgotPasswordPage")),
    isPublic: true
  },
  {
    path: "/account-deleted",
    component: loadable(() => import("./pages/Account/DeleteAccount")),
    isPublic: true
  },
  {
    path: "/verify-email",
    component: loadable(() => import("./components/pages/VerifyEmail")),
    isPublic: true
  },
  {
    path: "/login",
    component: loadable(() => import("./pages/Anonymous/Login")),
    isPublic: true
  },
  {
    path: "/",
    component: loadable(() => import("./pages/Anonymous/Root")),
    isPublic: true
  },

  {
    path: "/payment-reminder",
    component: loadable(() =>
      import("./pages/PaymentReminder/PaymentReminderConfirmation")
    ),
    isPublic: true
  },
  {
    path: "/auth/token",
    component: loadable(() => import("./pages/Anonymous/TokenAuth")),
    isPublic: true
  },
  {
    path: "/auth/nhs-login",
    component: loadable(() => import("./pages/NHSLogin/NHSLoginAuth")),
    isPublic: true
  },
  {
    path: "/nhs-login/confirm",
    component: loadable(() =>
      import("./pages/NHSLogin/NHSLoginExistingAccount")
    ),
    isPublic: true
  },
  {
    path: "/nhs-login/create",
    component: loadable(() => import("./pages/NHSLogin/NHSLoginCreateAccount")),
    isPublic: true
  },
  {
    path: "*/home*",
    component: () => <Redirect to="/" />
  },
  {
    path: "/universal",
    component: () => <Redirect to="/login" />,
    isPublic: true
  },
  {
    path: "*",
    component: loadable(() => import("./components/pages/NotFoundPage")),
    isPublic: true
  }
];

export const authenticatedRoutes = routes.filter(r => !r.isPublic);
export const publicRoutes = routes.filter(r => r.isPublic);
